import { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { createSlice } from '@reduxjs/toolkit';
import { convertFileToFormData } from 'components/util/convert';
import {
  getFilterQueryParam,
  getOrderbyQueryParam,
  getSkipQueryParam,
  getTopQueryParam
} from 'utils/getQueryParams';
import { Respondent, RespondentDataState } from '../../@types/respondent';
import axios from '../../utils/axios';
import { dispatch } from '../store';
import { rejectWithError } from '../dispatchError';
import { getDropzoneError } from 'components/CustomDropzone';
import { translator } from 'translation/useTranslator';
import { isUsageLimitReached } from 'utils/errorMessageHandling';

const initialState: RespondentDataState = {
  isLoading: false,
  isDeleteLoading: false,
  isMergeLoading: false,
  isSendSurveyLoading: false,
  isSampleRespondentsFromSpreadsheetLoading: false,
  respondentData: { count: 0, respondents: [], respondentsSampleFromSpreadsheet: undefined },
  isRespondentLoading: false,
  respondent: undefined
};

const slice = createSlice({
  name: 'respondent',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    stopLoading(state) {
      state.isLoading = false;
    },

    startDeleteLoading(state) {
      state.isDeleteLoading = true;
    },

    stopDeleteLoading(state) {
      state.isDeleteLoading = false;
    },

    startLoadingSendSurvey(state) {
      state.isSendSurveyLoading = true;
    },

    stopLoadingSendSurvey(state) {
      state.isSendSurveyLoading = false;
    },

    startSampleRespondentsFromSpreadsheetLoading(state) {
      state.isSampleRespondentsFromSpreadsheetLoading = true;
    },

    stopSampleRespondentsFromSpreadsheetLoading(state) {
      state.isSampleRespondentsFromSpreadsheetLoading = false;
    },

    // GET RESPONDENTS
    getRespondentsSuccess(state, action) {
      state.isLoading = false;
      state.respondentData.count = action.payload['@odata.count'];
      state.respondentData.respondents = action.payload.value;
    },

    editRespondentSuccess(state, action) {
      const newRes = JSON.parse(action.payload.config.data);
      state.respondentData.respondents = state.respondentData.respondents.map((res) =>
        newRes.id === res.id ? newRes : res
      );
      state.isLoading = false;
    },

    addRespondentSuccess(state, action) {
      state.isLoading = false;
    },

    addRespondentsSampleFromSpreadsheet(state, action) {
      state.respondentData.respondentsSampleFromSpreadsheet = action.payload;
      state.isSampleRespondentsFromSpreadsheetLoading = false;
    },

    startMerge(state) {
      state.isMergeLoading = true;
    },

    mergeDone(state) {
      state.isMergeLoading = false;
    },

    startGetRespondent(state) {
      state.isRespondentLoading = true;
    },

    setRespondent(state, action) {
      state.isRespondentLoading = false;
      state.respondent = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { setRespondent } = slice.actions;

const getUsageLimitReachedMessage = () =>
  translator?.usageLimitReachedNoSurveysSent() ??
  'No surveys sent: the usage limit for this period has been reached. Contact your administrator or buy more usage.';

type GetRespondentProps = {
  id: number;
};
export const getRespondent = ({ id }: GetRespondentProps) => {
  return async () => {
    dispatch(slice.actions.startGetRespondent());
    try {
      const response = await axios.get(`/odata/respondents/${id}?$expand=employee,company`);

      dispatch(slice.actions.setRespondent(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.respondentFetched() ?? 'Respondent fetched'
      });
    } catch (error) {
      dispatch(slice.actions.setRespondent(undefined));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchRespondent() ?? 'Could not fetch respondent'
      });
    }
  };
};

export function getRespondents({
  sortModel,
  filterModel,
  top,
  skip
}: {
  sortModel: GridSortModel | undefined;
  filterModel: GridFilterModel | undefined;
  top: number;
  skip: number;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/odata/respondents?$count=true${getOrderbyQueryParam(sortModel)}${getFilterQueryParam(
          filterModel
        )}${getSkipQueryParam(skip)}${getTopQueryParam(top)}&$expand=employee`
      );
      dispatch(slice.actions.getRespondentsSuccess(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.respondentsFetched() ?? 'Respondents fetched'
      });
    } catch (error) {
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchRespondents() ?? 'Could not fetch respondents'
      });
    }
  };
}

type EditRespondentProps = {
  respondent: Respondent;
  shouldChangeSubscribed?: boolean;
};
export function editRespondent({ respondent, shouldChangeSubscribed }: EditRespondentProps) {
  dispatch(slice.actions.startLoading());
  return async () => {
    try {
      const response = await axios.put('/respondents/' + respondent.id, {
        ...respondent
      });
      if (shouldChangeSubscribed) {
        await axios.put(`/respondents/${respondent.id}/active/${respondent.active}`);
      }

      dispatch(slice.actions.editRespondentSuccess(response));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.respondentUpdated() ?? 'Respondent updated'
      });
    } catch (error: any) {
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotUpdateRespondent() ?? 'Could not update respondent'
      });
    }
  };
}

type UnsubscribeRespondentProps = {
  publicGuid: string;
};
export function unsubscribeRespondent({ publicGuid }: UnsubscribeRespondentProps) {
  dispatch(slice.actions.startLoading());
  return async () => {
    try {
      const response = await axios.put('/respondents/' + publicGuid + '/unsubscribe');

      dispatch(slice.actions.stopLoading());

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.respondentUnsubscribed() ?? 'Respondent unsubscribed'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotUnsubscribeRespondent() ?? 'Could not unsubscribe respondent'
      });
    }
  };
}

export function deleteRespondents(ids: number[]) {
  return async () => {
    dispatch(slice.actions.startDeleteLoading());
    try {
      for (let id of ids) {
        await axios.delete('/respondents/' + id);
      }
      dispatch(slice.actions.stopDeleteLoading());

      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.respondentsDeleted(`${ids.length}`) ??
          `${ids.length} respondent${ids.length > 1 ? 's' : ''} deleted`
      });
    } catch (error: any) {
      dispatch(slice.actions.stopDeleteLoading());

      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotDeleteRespondent() ?? 'Could not delete respondent'
      });
    }
  };
}

export function addRespondent({
  respondent,
  campaignId
}: {
  respondent: Partial<Respondent>;
  campaignId: number;
}) {
  dispatch(slice.actions.startLoading());
  return async () => {
    try {
      const response = await axios.post(`/campaigns/${campaignId}/respondents`, [respondent]);
      dispatch(slice.actions.addRespondentSuccess(response));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.respondentAdded() ?? 'Respondent added'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      return rejectWithError({
        error: error,
        defaultErrorMessage: isUsageLimitReached(error)
          ? getUsageLimitReachedMessage()
          : translator?.couldNotAddRespondent() ?? 'Could not add respondent'
      });
    }
  };
}

export function sampleRespondentsFromSpreadsheet({
  campaignId,
  file
}: {
  campaignId: number;
  file: File | undefined;
}) {
  return async () => {
    dispatch(slice.actions.startSampleRespondentsFromSpreadsheetLoading());
    try {
      if (!file) {
        dispatch(slice.actions.addRespondentsSampleFromSpreadsheet({}));
        return await Promise.resolve({
          defaultSuccessMessage: translator?.noFileUploaded() ?? 'No file uploaded'
        });
      }

      const response = await axios.post(
        `/campaigns/${campaignId}/respondents/fromspreadsheet/sample`,
        convertFileToFormData(file)
      );
      dispatch(slice.actions.addRespondentsSampleFromSpreadsheet(response.data));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.yourCampaignWasScheduled() ?? 'Your campaign was scheduled'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopSampleRespondentsFromSpreadsheetLoading());
      return await rejectWithError({
        error: error,
        defaultErrorMessage: getDropzoneError(
          translator?.uploadedFileCouldNotBeProcessed() ?? 'Uploaded file could not be processed.'
        )
      });
    }
  };
}

export function sendOutCampaignFromSpreadsheet({
  campaignId,
  file,
  sendEmailAt
}: {
  campaignId: number;
  file: File | undefined | null;
  sendEmailAt: Date;
}) {
  return async () => {
    dispatch(slice.actions.startLoadingSendSurvey());
    try {
      const response = await axios.post(
        `/campaigns/${campaignId}/respondents/fromspreadsheet?sendEmailAt=${sendEmailAt.toISOString()}`,
        convertFileToFormData(file)
      );
      dispatch(slice.actions.stopLoadingSendSurvey());
      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.processingOfNewCampaignMembersStarted() ??
          'Processing of new campaign members started',
        queuedCount: response?.data?.queuedCount as number | undefined
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoadingSendSurvey());
      return await rejectWithError({
        error: error,
        defaultErrorMessage: isUsageLimitReached(error)
          ? getUsageLimitReachedMessage()
          : translator?.couldNotScheduleCampaign() ?? 'Could not schedule campaign'
      });
    }
  };
}

type MergeRespondentsProps = {
  sourceId: number;
  targetId: number;
};
export function mergeRespondents({ sourceId, targetId }: MergeRespondentsProps) {
  return async () => {
    dispatch(slice.actions.startMerge());
    try {
      await axios.put(`/respondents/merge?sourceId=${sourceId}&targetId=${targetId}`);
      dispatch(slice.actions.mergeDone());
      return await Promise.resolve({
        defaultSuccessMessage: translator?.respondentsMerged() ?? 'Respondents merged'
      });
    } catch (error: any) {
      dispatch(slice.actions.mergeDone());
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotMergeRespondents() ?? 'Could not merge respondents'
      });
    }
  };
}
